import React, { useMemo, useRef, useState } from "react";

import * as Popover from "@radix-ui/react-popover";
import { cx } from "class-variance-authority";
import Downshift, { GetItemPropsOptions } from "downshift";
import Fuse from "fuse.js";
import { get, groupBy, has } from "lodash";
import { FixedSizeList as List } from "react-window";
import useResizeObserver from "use-resize-observer";
import { useVirtualizedDownshiftStateReducer } from "../typeahead/ shared";

const errors = {
  newItemHandlerWithoutTitle: `Found an onCreateNewItem handler but no new item title.

  If you want to create new items you must provide a title for the new item using the "newItemOverride" prop, or enable the search bar by passing in "searchable".`,
  noNewItemHandlerForOveride: `Found a newItemOverride but no onCreateNewItem handler.`,
};

export interface SelectOption {
  label: string;
  value: string;
  textClass?: string;
  group?: string;
  isGroupHeader?: boolean;
}

export interface VirtualSelectProps {
  // Basic use case props
  open: boolean;
  modal?: boolean;
  options: SelectOption[];
  onOpenChange: (open: boolean) => void;
  onSelectItem?: (item: SelectOption) => void;
  onCreateNewItem?: (itemName: string, event: Event) => void;
  searchable?: boolean;
  children?: React.ReactNode;
  width?: number;
  isItemSelected: (item: SelectOption) => boolean;
  filterFunction?: (item: SelectOption, search: string) => boolean;
  newItemOverride?: {
    itemTitle?: string;
    label?: string;
  };
  noOptionsMessage?: string;
  noResultsMessage?: string;
  side?: "top" | "bottom" | "right" | "left";
  sideOffset?: number;
  avoidCollisions?: boolean;
  disabled?: boolean;
}

interface MenuItemProps {
  index: number;
  style: any;
  data: {
    onSelect: (value: SelectOption) => void;
    items: SelectOption[];
    getItemProps: (item: GetItemPropsOptions<any>) => void;
    option: SelectOption[];
    highlightedIndex: number;
    isItemSelected: (item: SelectOption) => boolean;
  };
}

// Downshift takes a function as a child, we're creating a function that returns a component
// that we can pass to Downshift and memoizing it so that it only re-mounts when the props change
const ItemRenderer = React.memo((props: MenuItemProps) => {
  const item = props.data.items[props.index];
  const isHighlighted = props.index === props.data.highlightedIndex;
  const isSelected = props.data.isItemSelected(item);
  if (item.isGroupHeader) {
    return (
      <li
        role="menuitem"
        style={{
          ...props.style,
          cursor: "default",
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
        className={cx("group flex w-full !cursor-default rounded text-navy")}
      >
        <div
          className={cx(
            "space-x-2 px-3 pt-1",
            "flex w-full cursor-pointer items-center ",
            "rounded text-sm font-semibold",
          )}
        >
          <span className="truncate">{item.label}</span>
        </div>
      </li>
    );
  }

  return (
    <li
      role="menuitem"
      {...(props.data.getItemProps({
        item,
        index: props.index,
        style: props.style,
      }) as unknown as object)}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        props.data.onSelect(item);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          e.stopPropagation();
          props.data.onSelect(item);
        }
      }}
      className={cx(
        "group flex w-full rounded",
        isHighlighted ? "bg-friendly-eskimo" : "",
        isSelected ? "text-navy" : "",
      )}
    >
      <div
        className={cx(
          "space-x-2 px-3 py-2",
          "flex w-full cursor-pointer items-center text-sm",
          "rounded text-base",
        )}
      >
        <span className="truncate">{item.label}</span>
      </div>
    </li>
  );
});

const removePunctuation = (punctuatedString: string) => {
  return punctuatedString?.replace(/[.,/#!$%^&*;:{}=\-_`~()]/g, "");
};

const ITEM_HEIGHT = 42;
const MIN_WIDTH = 300;
const MAX_HEIGHT = 300;
// const EMPTY_ARRAY = [];
// Virtualized select component to handle huge lists
// Tested with upto 15k items
export const VirtualizedSelect = (props: VirtualSelectProps) => {
  // Create a reference to the underlying list so we can scroll to the selected item
  // as the keyboard is used to navigate
  const listRef = useRef<any>();
  // Virtual list needs a set width, we render a hidden element with the
  // first item from the list to get a width
  // TODO: Find a better way to get the width of the list
  const { width = 1, ref } = useResizeObserver();

  const [searchString, setInput] = useState("");

  // We need to filter the items before we pass them into downshift
  // This allows us to keep the virtualization indices in sync with the actual items
  // that are rendered

  // const filteredItems = (props.options ?? EMPTY_ARRAY).filter(
  //   (item) => item?.label?.toLowerCase()?.includes(searchString?.toLowerCase()),
  // );

  // Previously, we used the code above to filter the items, but now we will use fuzzy search
  // to return more results and allow the user to search with more broad terms

  const fuse = useMemo(() => {
    const fuseOptions = {
      keys: ["label"], // Specify which keys to search in your SelectOption objects
      threshold: 0.4,
      includeScore: true,
      // Record<string, any>
      getFn: (obj: SelectOption | undefined) =>
        removePunctuation(obj?.label ?? ""),
    };
    return new Fuse(props.options, fuseOptions);
  }, [props.options]);

  const filteredItems =
    searchString.trim() === ""
      ? props.options
      : fuse
          .search(removePunctuation(searchString))
          .map((result) => result.item);

  // If we have a createNewItem function and some sort of title to show
  // This allows us to support new items without the search bar being required
  const hasNewItemTitleOverride = Boolean(props?.newItemOverride?.itemTitle);
  const hasRequiredTitleProps = hasNewItemTitleOverride || props.searchable;
  const hasNewItemHandler = props?.onCreateNewItem !== undefined;

  if (hasNewItemHandler && !hasRequiredTitleProps) {
    throw new Error(errors.newItemHandlerWithoutTitle);
  }

  if (!hasNewItemHandler && hasNewItemTitleOverride) {
    throw new Error(errors.noNewItemHandlerForOveride);
  }

  const hasNewItemTitle = hasNewItemTitleOverride || Boolean(searchString);
  const showCreateNewItem = hasNewItemHandler && hasNewItemTitle;

  const collectGroups = groupBy(filteredItems, (o) => o.group ?? "ungrouped");

  const flatRows: SelectOption[] = [];
  Object.keys(collectGroups).forEach((group) => {
    if (group === "ungrouped") {
      flatRows.push(...collectGroups[group]);
    } else {
      flatRows.push({
        label: group,
        isGroupHeader: true,
        value: group,
      });
      flatRows.push(...collectGroups[group]);
    }
  });

  // Downshift gives us access to various events
  // We are:
  //  selecting the item on enter
  //  setting the search string for the input so we can filter items
  //  scrolling to the selected item in the virtual list
  const handleStateChange = (changes) => {
    // TODO: remove usage of this hardcoded string and number
    if (
      changes.type === "__autocomplete_keydown_enter__" ||
      changes.type === 6
    ) {
      props.onSelectItem(changes.selectedItem);
    }
    if (changes.type === "__autocomplete_change_input__") {
      setInput(changes.inputValue ?? "");
    }
    if (has(changes, "highlightedIndex")) {
      if (listRef.current) {
        listRef.current.scrollToItem(changes.highlightedIndex);
      }
    }
  };

  const stateReducer = useVirtualizedDownshiftStateReducer({
    options: flatRows,
  });

  const MENU_WIDTH = Math.max(props.width ?? width, MIN_WIDTH);

  return (
    <Popover.Root
      modal={props.modal}
      open={props.open}
      onOpenChange={props.onOpenChange}
    >
      <Popover.Trigger disabled={props.disabled}>
        <div ref={ref} className="w-full">
          {props.children}
        </div>
      </Popover.Trigger>
      <Popover.Content
        style={{ zIndex: 8000 }}
        sideOffset={props.sideOffset}
        side={props.side}
        avoidCollisions={props.avoidCollisions}
      >
        <Downshift
          // Hard code this to true because we're handling the popover logic using radix
          isOpen={true}
          itemCount={flatRows.length}
          stateReducer={stateReducer}
          // we are creating a controlled component so we don't use this
          // setting it to null prevents some default behavior in downshift
          selectedItem={null}
          onStateChange={(state) => handleStateChange(state)}
        >
          {({
            getInputProps, // returns accessible props for input
            getItemProps, // returns accessible props for each item
            highlightedIndex,
            selectedItem,
            getRootProps, // returns accessible props for the root
          }) => {
            return (
              <div
                // @ts-ignore
                {...getRootProps({}, { suppressRefError: true })}
                className={cx(
                  "relative rounded-md border border-dublin-gray bg-white shadow",
                )}
              >
                {/* This is a dummy element to set the width of the list */}
                <div
                  className="pointer-events-none absolute top-0 whitespace-nowrap px-6 opacity-0"
                  aria-hidden="true"
                >
                  {get(props.options, "0.label", "")} {width}
                </div>

                {props.searchable && (
                  <div>
                    <div
                      className="w-full px-2 py-2"
                      style={{ width: MENU_WIDTH }}
                    >
                      <input
                        {...getInputProps({
                          placeholder: "Search...",
                          className:
                            "border w-full rounded border-dublin-gray text-base text-charcoal py-2 px-4",
                          autoFocus: true,
                          value: searchString,
                          onKeyDown: (e) => {
                            if (e.key === "Escape") {
                              props.onOpenChange(false);
                            }
                          },
                          onChange: (e) => {
                            setInput(e.target.value);
                          },
                        })}
                      />
                    </div>
                  </div>
                )}
                {showCreateNewItem ? (
                  <div
                    role="menuitem"
                    className={cx(
                      "group rounded",
                      "focus:bg-friendly-eskimo focus:outline-none",
                      "focus:text-navy",
                      "hover:text-navy",
                      "text-base",
                      "cursor-pointer",
                    )}
                    style={{ width: MENU_WIDTH }}
                    onSelect={(event: any) =>
                      props.onCreateNewItem(
                        props.newItemOverride?.itemTitle ?? searchString,
                        event,
                      )
                    }
                  >
                    <div className="flex items-center space-x-2 px-3 py-2 text-navy-400">
                      <span className="whitespace-nowrap">
                        {props.newItemOverride?.label ?? "Create new item:"}
                      </span>
                      <span className="ml-2 truncate">
                        {props.newItemOverride?.itemTitle ?? searchString}
                      </span>
                    </div>
                  </div>
                ) : null}
                {filteredItems?.length === 0 ? (
                  <div className="mx-auto max-w-[220px] overflow-hidden truncate py-2 text-center text-sm text-medium-hippo">
                    {searchString.length > 0
                      ? (props.noResultsMessage ?? "No results found")
                      : (props.noOptionsMessage ?? "No options available")}
                  </div>
                ) : null}
                <List
                  ref={listRef}
                  width={MENU_WIDTH}
                  className="styled-scroll"
                  height={Math.min(flatRows.length * ITEM_HEIGHT, MAX_HEIGHT)}
                  itemCount={flatRows.length}
                  itemSize={ITEM_HEIGHT}
                  itemData={{
                    items: flatRows,
                    getItemProps,
                    selectedItem,
                    highlightedIndex,
                    onSelect: props.onSelectItem,
                    isItemSelected: props.isItemSelected,
                  }}
                >
                  {ItemRenderer}
                </List>
              </div>
            );
          }}
        </Downshift>
      </Popover.Content>
    </Popover.Root>
  );
};
